import type { FieldValues } from 'react-hook-form';
import type { Widget } from 'jamespot-user-api';
import type { NameControl } from 'jamespot-react-components';
import { UseFormReturn } from 'react-hook-form';
import { FunctionComponent, ReactNode } from 'react';

export type IncludeCustom = { includes: 'tags' | '_attachedFiles' };

export type FieldConfig = {
    disableSortBy?: boolean;
};

export type FieldConfigExtra = Omit<DisplayerElementAttributes, 'name'>;

export const CONFIGURABLE_FIELDS_NAMES = ['publishTo'];
export type ConfigurableFieldName = (typeof CONFIGURABLE_FIELDS_NAMES)[number];
export type ConfigurableField<N extends ConfigurableFieldName, T extends object> = { name: N; options: T };
export type ConfigurableAudience = ConfigurableField<'publishTo', { disableEveryoneAudience?: boolean }>;
export type ConfigurableFields = ConfigurableAudience; // | ...
export const ensureConfigurableField = (field: unknown): field is ConfigurableFields =>
    !!field &&
    typeof field === 'object' &&
    'name' in field &&
    typeof field.name === 'string' &&
    CONFIGURABLE_FIELDS_NAMES.includes(field.name);

export type FieldExtra = {
    name: string;
    extra: FieldConfigExtra;
};

export type Field<TFieldValues extends FieldValues = FieldValues> =
    | string
    | FieldExtra
    | {
          name: string;
          componentsOverride: Partial<DisplayElementComponent<TFieldValues>>;
          config?: FieldConfig;
      }
    | IncludeCustom
    | ConfigurableFields;

export type Fields<TFieldValues extends FieldValues = FieldValues> = Array<Field<TFieldValues>>;

export type FieldWith<With extends 'input' | 'render', TFieldValues extends FieldValues = FieldValues> =
    | string
    | ({ [x in With]: DisplayElementComponent<TFieldValues>[With] } & {
          name: string;
          config?: FieldConfig;
      })
    | FieldExtra
    | IncludeCustom
    | ConfigurableFields;
export type FieldsWith<With extends 'input' | 'render', TFieldValues extends FieldValues = FieldValues> = Array<
    FieldWith<With, TFieldValues>
>;

export type RenderAttributeProps = {
    object: any;
    attribute: DisplayerElementAttributes;
};

export type DisplayInputComponentProps<TFieldValues extends FieldValues = FieldValues> = NameControl<TFieldValues> & {
    label: string;
    placeholder?: string;
    description?: string;
    widget: Widget;
    mandatory: boolean;
    dataCy?: string;
    width?: string;
    margin?: string;
    readOnly?: boolean;
};

export type DisplayElementComponent<TFieldValues extends FieldValues = FieldValues> = {
    render: FunctionComponent<RenderAttributeProps>;
    input: FunctionComponent<DisplayInputComponentProps<TFieldValues>>;
    search: FunctionComponent<DisplayInputComponentProps<TFieldValues>>;
    extra?: FieldConfigExtra;
};

export type DisplayerElementAttributes = {
    name: string;
    label: string;
    description?: string;
    mandatory: boolean;
    widget: Widget;
};

export type DisplayerElement<TFieldValues extends FieldValues = FieldValues> = DisplayerElementAttributes & {
    components: DisplayElementComponent<TFieldValues>;
};

export type DisplayFormProps<TFieldValues extends FieldValues = FieldValues> = {
    type: string;
    componentsOverride?: Record<string, Partial<DisplayElementComponent<TFieldValues>>>;
    attributesName: string[];
    onSubmit: any;
    buttons: ReactNode;
};

export type DisplayFormRef = UseFormReturn<any> & { getFormattedValues: () => any };

export type Displayer<TFieldValues extends FieldValues = FieldValues> = Array<DisplayerElement<TFieldValues>>;

export type DisplayFormFieldsConfig<TFieldValues extends FieldValues> = 'all' | FieldsWith<'input', TFieldValues>;
